import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIconButton from '../components/DeleteIconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useIsWidthUp } from '../utils/use_width_up';
import {
    Divider,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { DataAPIContext, GET_LIST, GET_ONE } from '../api';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormActionButtons from '../components/FormActionButtons';
import { FIELD_TYPES, customNoteLast, normalizePreCheckSettings, toBool } from './preCheckSettings';
import { ConfirmationDialogText } from './utils';
import { ContextStore } from '../store/contextStore';
import { tourUsesOperatorDefaults } from '../utils/tagCategories';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '8rem !important',
            '@supports (-webkit-touch-callout: none)': {
                paddingBottom: 'max(12rem, calc(10rem + env(safe-area-inset-bottom, 2rem))) !important',
            },
        },
    },
    styledBox: {
        padding: '2rem',
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '12px',
        margin: '0rem 1rem 1rem 1rem',
        [theme.breakpoints.down('sm')]: {
            padding: '1rem',
            margin: '0rem',
            overflowY: 'auto',
        },
        [theme.breakpoints.up('sm')]: {
            border: '1px solid #D8D8D8',
        },
    },
    fieldItem: {
        border: '1px solid #D0D5DD',
        borderRadius: '8px',
        padding: '8px 6px 8px 6px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        backgroundColor: 'white',
    },
    fieldControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    agreementItem: {
        borderRadius: '8px',
        padding: '8px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'white',
    },
    waiverItem: {
        borderRadius: '8px',
        padding: '4px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
        backgroundColor: 'white',
    },
    fieldsEditor: {
        position: 'fixed',
        right: 0,
        top: 0,
        width: '427px',
        height: '100vh',
        backgroundColor: 'white',
        borderLeft: '1px solid #D8D8D8',
        zIndex: 1300,
        overflowY: 'auto',
        boxShadow: '0px 4px 15px 0px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        [theme.breakpoints.down('sm')]: {
            width: 'calc(100vw - 16px)',
            right: '8px',
            top: '64px',
            height: 'calc(100vh - 64px)',
        },
    },
    fieldsEditorOpen: {
        transform: 'translateX(0)',
    },
    fieldsEditorHeader: {
        backgroundColor: '#F5F5F5',
        padding: '20px 28px 20px 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #D8D8D8',
        [theme.breakpoints.down('sm')]: {
            padding: '16px 20px',
        },
    },
    fieldsEditorContent: {
        padding: '20px 28px',
        flex: 1,
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: '16px 20px',
        },
    },
    fieldsEditorBottom: {
        backgroundColor: '#F5F5F5',
        borderTop: '1px solid #D8D8D8',
        padding: '12px 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            padding: '12px 20px',
        },
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1299,
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
    },
    overlayVisible: {
        opacity: 1,
    },
    loadingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    expandableFieldItem: {
        border: '1px solid #D0D5DD',
        borderRadius: '8px',
        padding: '8px 12px 12px 12px',
        marginBottom: '8px',
        backgroundColor: 'white',
    },
    tagCategoriesDropdown: {
        backgroundColor: '#F5F5F5',
        border: '0px 0px 1px solid #747475',
        borderRadius: '8px',
        padding: '8px 12px',
        marginTop: '8px',
    },
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        marginBottom: '4px',
    },
    applyToPartyMembersSection: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '8px',
        marginTop: '36px',
        marginLeft: '10px',
        [theme.breakpoints.down('sm')]: {
            marginTop: '24px',
        }
    },
    fieldContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px', // 0.75 * 8px = 6px
    },
}));

// Helper function to get all possible fields for editor
const getAllFieldTypes = (fieldType) => {
    if (fieldType === 'lead_booker') {
        return FIELD_TYPES.filter(f => f.id !== 'is_minor');
    } else {
        // For party members, show all fields but is_minor should be first (for UX)
        const minorField = FIELD_TYPES.find(f => f.id === 'is_minor');
        const otherFields = FIELD_TYPES.filter(f => f.id !== 'is_minor');
        return minorField ? [minorField, ...otherFields] : FIELD_TYPES;
    }
};

// Helper function to check if field is mandatory
const isFieldMandatory = (fieldId, fieldType) => {
    if (fieldType === 'lead_booker') {
        const field = FIELD_TYPES.find(f => f.id === fieldId.replace(/_\d+$/, ''));
        return field ? field.mandatoryForLead : false;
    } else {
        // For party members, no fields are mandatory anymore
        return false;
    }
};

// Custom pre-check-in fields: any row that is neither one of the standard FIELD_TYPES nor a
// tag-category question (the backend's rule, demo/lib/precheckin/fields.py). The console defines
// them; here they are listed, kept, toggled and reordered, never created.
const STANDARD_FIELD_IDS = new Set(FIELD_TYPES.map((f) => f.id));
const CUSTOM_FIELD_TYPES = ['string', 'text', 'number', 'date', 'boolean'];

export const isQuestionRow = (row) => !!row && row.type === 'tag_category';

// A question's category code: category_id, else the id without its tag_category_ prefix.
export const questionCode = (row) => {
    if (row.category_id != null && row.category_id !== '') return row.category_id;
    return typeof row.id === 'string' ? row.id.replace(/^tag_category_/, '') : null;
};

export const isCustomRow = (row) => !!row
    && typeof row.id === 'string'
    && row.id !== ''
    && !STANDARD_FIELD_IDS.has(row.id)
    && row.type !== 'tag_category'
    && row.type !== 'category';

const isStandardRow = (row) => !!row && STANDARD_FIELD_IDS.has(row.id) && !isQuestionRow(row) && row.type !== 'category';

// The fields panel's custom rows: those in the list being edited (checked, with their stored
// label), then the operator's other definitions (unchecked) -- only those of a type the guest
// app can answer.
export const customPanelRows = (list, definitions) => {
    const rows = [];
    const seen = new Set();
    (list || []).forEach((row) => {
        if (isCustomRow(row) && !seen.has(row.id)) {
            seen.add(row.id);
            rows.push({ id: row.id, name: row.name || row.id, type: row.type, required: !!row.required, enabled: true, custom: true });
        }
    });
    (definitions || []).forEach((def) => {
        if (isCustomRow(def) && !seen.has(def.id) && CUSTOM_FIELD_TYPES.includes(def.type)) {
            seen.add(def.id);
            rows.push({ id: def.id, name: def.name || def.id, type: def.type, required: false, enabled: false, custom: true });
        }
    });
    return rows;
};

// For party members, is_minor stays first (the page shows it there and keeps it there).
const minorFirst = (list) => {
    const minor = list.find((row) => row && row.id === 'is_minor' && !isQuestionRow(row));
    return minor ? [minor, ...list.filter((row) => row !== minor)] : list;
};

// The list the fields panel's Apply leaves. Rows that stay keep their place and everything
// they carry (label, type, flags); rows the panel unchecked go; newly checked fields, then newly
// selected questions, are appended in panel order; a row the panel does not show (a question
// whose category it cannot offer, a stored "category" row, ...) is kept as it is.
//   panelRows: the standard and custom rows ({id, name, type, required, enabled, custom?});
//   questions: {offered: Set of category codes the panel shows, selected: [code] (empty when
//              questions are off), make: code => a new question row or null};
//   isMandatory: row => kept whatever its checkbox.
export const applyFieldsPanel = (current, panelRows, questions, isMandatory) => {
    const offered = new Set(panelRows.map((row) => row.id));
    const wanted = panelRows.filter((row) => row.enabled || isMandatory(row));
    const wantedIds = new Set(wanted.map((row) => row.id));
    const selected = new Set(questions.selected);
    const out = [];
    const kept = new Set();
    const keptCodes = new Set();
    (current || []).forEach((row) => {
        if (isQuestionRow(row) && questions.offered.has(questionCode(row))) {
            const code = questionCode(row);
            if (selected.has(code) && !keptCodes.has(code)) {
                keptCodes.add(code);
                out.push(row);
            }
            return;
        }
        if ((isStandardRow(row) || isCustomRow(row)) && offered.has(row.id)) {
            if (wantedIds.has(row.id) && !kept.has(row.id)) {
                kept.add(row.id);
                out.push(row);
            }
            return;
        }
        out.push(row);
    });
    wanted.forEach((row) => {
        if (!kept.has(row.id)) {
            kept.add(row.id);
            out.push({ id: row.id, name: row.name, type: row.type, required: !!row.required });
        }
    });
    questions.selected.forEach((code) => {
        if (keptCodes.has(code)) return;
        const row = questions.make(code);
        if (row) {
            keptCodes.add(code);
            out.push(row);
        }
    });
    return out;
};

// "Apply same fields to all party members": the party gets the lead booker's list -- custom
// fields included -- keeping its own is_minor (first), its own row for a question both ask, and
// any row of its own the panel does not show. The custom note stays last.
export const sameFieldsForParty = (leadList, partyList, offeredCodes) => {
    const party = partyList || [];
    const minor = party.find((row) => row && row.id === 'is_minor' && !isQuestionRow(row));
    const partyQuestions = new Map(party.filter(isQuestionRow).map((row) => [questionCode(row), row]));
    const out = minor ? [minor] : [];
    const ids = new Set(['is_minor']);
    const codes = new Set();
    const add = (row) => {
        if (isQuestionRow(row)) {
            const code = questionCode(row);
            if (codes.has(code)) return;
            codes.add(code);
            out.push(partyQuestions.get(code) || row);
        } else if (row && typeof row.id === 'string') {
            if (ids.has(row.id)) return;
            ids.add(row.id);
            out.push(row);
        } else {
            out.push(row);
        }
    };
    leadList.forEach(add);
    party
        .filter((row) => (isQuestionRow(row) ? !offeredCodes.has(questionCode(row)) : !isStandardRow(row) && !isCustomRow(row)))
        .forEach(add);
    return customNoteLast(out);
};

// CP-7540, operator mode only: `onApplyToAllTrips` (absent without tour_operator.write + tour.write) runs
// Settings › Pre-check-in's "Apply to all trips" and returns a promise; `applyToAllTripsBlocked` says why the
// button is disabled (unsaved changes: the trips would get the saved defaults, not the ones on screen).
const TripPreCheckSettings =({ data, onChange, hasWritePerm, onSave, mode = 'trip', onApplyToAllTrips, applyToAllTripsBlocked }) => {

    const classes = useStyles();
    const theme = useTheme();
    const dataAPI = useContext(DataAPIContext);
    const { tourOperator } = useContext(ContextStore);
    const trip = data;
    const isOperatorMode = mode === 'operator';

    const [waivers, setWaivers] = useState([]);
    const [waiversLoading, setWaiversLoading] = useState(true);
    // Both feed the Add Waiver prompt (CP-6579): a failed load is not "no
    // waivers", and "none enabled" is not "none created".
    const [waiversLoadFailed, setWaiversLoadFailed] = useState(false);
    const [hasDisabledWaivers, setHasDisabledWaivers] = useState(false);
    const [waiversReloadKey, setWaiversReloadKey] = useState(0);
    const [waiverTemplateMeta, setWaiverTemplateMeta] = useState(null);
    const [fieldsEditorOpen, setFieldsEditorOpen] = useState(false);
    const [currentEditingField, setCurrentEditingField] = useState(null); // 'lead_booker' or 'party_member'
    const [tagCategoriesExpanded, setTagCategoriesExpanded] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(trip._changed || false);
    const [confirmationDialogAction, setConfirmationDialogAction] = useState(null);
    const [applyToAllOpen, setApplyToAllOpen] = useState(false);
    const [applyingToAll, setApplyingToAll] = useState(false);

    // Local state for fields editor
    const [editingFields, setEditingFields] = useState([]);
    const [editingTagCategoriesEnabled, setEditingTagCategoriesEnabled] = useState(false);
    const [editingSelectedTagCategories, setEditingSelectedTagCategories] = useState([]);
    const [editingApplySameFields, setEditingApplySameFields] = useState(false);
    // CP-8441: what the fields editor held when it opened. SAVE waits until the editor differs from
    // it, and turns off again if the user puts everything back -- the hasUnsavedChanges flag only ever
    // switches on, so it can't do that.
    const fieldsEditorSnapshot = (fields, tagCategoriesEnabled, selectedTagCategories, applySameFields) => JSON.stringify({
        fields: (fields || []).map((f) => [f.id, !!f.enabled, !!f.required]),
        tagCategoriesEnabled: !!tagCategoriesEnabled,
        selectedTagCategories: selectedTagCategories || [],
        applySameFields: !!applySameFields,
    });
    const [fieldsEditorBaseline, setFieldsEditorBaseline] = useState(null);
    const fieldsEditorChanged = fieldsEditorBaseline !== null
        && fieldsEditorSnapshot(editingFields, editingTagCategoriesEnabled, editingSelectedTagCategories, editingApplySameFields) !== fieldsEditorBaseline;
    const [isFieldsEditorAnimating, setIsFieldsEditorAnimating] = useState(false);

    const isSmallScreen = !useIsWidthUp('sm');

    // Variables menu state for confirmation message
    const [anchorElConfirmVar, setAnchorElConfirmVar] = useState(null);
    const [menuPathConfirm, setMenuPathConfirm] = useState([]);
    const confirmInputRef = React.useRef(null);
    const [confirmSelection, setConfirmSelection] = useState({ start: null, end: null });

    // Track when we need to save after state update
    const pendingSaveRef = React.useRef(false);

    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setWaiversLoading(true);
            try {
                const [listRes, templateRes] = await Promise.all([
                    dataAPI(GET_LIST, 'waiver', { urlSuffix: null }),
                    dataAPI(GET_ONE, 'waiver', { urlSuffix: 'template_variables/' }),
                ]);
                if (cancelled) {
                    return;
                }
                const rows = Array.isArray(listRes?.data) ? listRes.data : [];
                setWaivers(rows.filter((w) => w.enabled));
                setHasDisabledWaivers(rows.some((w) => !w.enabled));
                setWaiversLoadFailed(false);
                const tv = templateRes?.data?.waivers_variables;
                setWaiverTemplateMeta(
                    tv && typeof tv === 'object' && Object.keys(tv).length > 0 ? templateRes.data : null,
                );
            } catch (error) {
                console.error('Error loading waivers or template variables:', error);
                if (!cancelled) {
                    setWaivers([]);
                    setHasDisabledWaivers(false);
                    setWaiversLoadFailed(true);
                    setWaiverTemplateMeta(null);
                }
            } finally {
                if (!cancelled) {
                    setWaiversLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dataAPI, waiversReloadKey]);

    // Handle pending save after state update  
    React.useEffect(() => {
        if (pendingSaveRef.current && trip._changed) {
            pendingSaveRef.current = false;

            // State has been updated, now we can save
            if (onSave) {
                onSave();
            }
        }
    }, [trip._changed, onSave]);

    // Get tag categories from trip's associated tag categories
    const tagCategories = useMemo(() => {
        const seen = new Set();
        const usesOperatorDefaults = tourUsesOperatorDefaults(
            { use_operator_defaults: trip.use_operator_defaults },
            tourOperator
        );
        
        const operatorDefaults = usesOperatorDefaults ? (tourOperator?.default_tag_categories || []) : [];
        const allCategories = [...(trip.tag_categories || []), ...operatorDefaults];

        return allCategories
            .map(tc => tc.category)
            .filter(Boolean)
            .filter(category => {
                const key = category.code || category.id || category.name;
                if (!key) return false;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    }, [trip.tag_categories, trip.use_operator_defaults, tourOperator]);

    // The question categories the fields panel can offer, by code.
    const offeredQuestionCodes = useMemo(
        () => new Set(tagCategories.map((category) => category.code || category.id)),
        [tagCategories],
    );

    // A question row for a category newly selected in the fields panel.
    const makeQuestionRow = (categoryId) => {
        const category = tagCategories.find((c) => (c.code || c.id) === categoryId);
        if (!category) {
            console.warn(`Category not found for id: ${categoryId}`);
            return null;
        }
        return {
            id: `tag_category_${categoryId}`,
            name: category.name,
            type: 'tag_category',
            category_id: categoryId,
            category_name: category.name || '',
            schema: category.schema || {},
            required: false,
            multiselect: false,
        };
    };

    // Helper function to get current settings - single source of truth
    const preCheckSettings = useMemo(() => normalizePreCheckSettings(
        (!isOperatorMode ? (trip.pre_check_settings_own || trip.pre_check_settings) : trip.pre_check_settings) || {}
    ), [trip.pre_check_settings, trip.pre_check_settings_own, isOperatorMode]);

    const variablesTree = useMemo(() => {
        const tv = waiverTemplateMeta?.waivers_variables;
        return tv && typeof tv === 'object' && Object.keys(tv).length > 0 ? tv : null;
    }, [waiverTemplateMeta]);
    const hasVariables = useMemo(() => {
        return variablesTree && typeof variablesTree === 'object' && Object.keys(variablesTree).length > 0;
    }, [variablesTree]);
    const currentNodeConfirm = useMemo(() => {
        if (!variablesTree) return null;
        let node = variablesTree;
        for (const key of menuPathConfirm) {
            if (node && typeof node === 'object') {
                node = node[key];
            }
        }
        return node;
    }, [variablesTree, menuPathConfirm]);

    const openConfirmVariablesMenu = (event) => {
        if (!hasVariables) return;
        setAnchorElConfirmVar(event.currentTarget);
        setMenuPathConfirm([]);
    };
    const closeConfirmVariablesMenu = () => {
        setAnchorElConfirmVar(null);
        setMenuPathConfirm([]);
    };
    const handleConfirmMenuBack = () => {
        setMenuPathConfirm((prev) => prev.slice(0, -1));
    };
    const handleConfirmMenuClick = (key) => {
        if (!currentNodeConfirm) return;
        const value = currentNodeConfirm[key];
        if (value && typeof value === 'object') {
            setMenuPathConfirm((prev) => [...prev, key]);
        } else {
            const path = [...menuPathConfirm, key].join('.');
            insertIntoConfirmationAtCursor(`{{ ${path} }}`);
            closeConfirmVariablesMenu();
        }
    };

    const insertIntoConfirmationAtCursor = (text) => {
        if (!text) return;
        const input = confirmInputRef.current;
        const value = preCheckSettings.confirmation_message || '';
        const start = (confirmSelection.start ?? (input ? input.selectionStart : null)) ?? value.length;
        const end = (confirmSelection.end ?? (input ? input.selectionEnd : null)) ?? start;
        const newValue = value.slice(0, start) + text + value.slice(end);
        handleSettingsChange('confirmation_message', newValue);
        setTimeout(() => {
            if (input) {
                const pos = start + text.length;
                input.focus();
                try { input.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
            }
        }, 0);
    };

    const handleSettingsChange = (field, value) => {
        // Use preCheckSettings as base
        let updatedSettings = {
            ...preCheckSettings,
            [field]: value,
        };

        const updatedTrip = {
            ...trip,
            pre_check_settings: updatedSettings,
            ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
            _changed: true,
        };

        onChange(updatedTrip);
        setHasUnsavedChanges(true);
    };

    const handleCheckInFlowToggle = (enabled) => {
        if (!enabled && !isOperatorMode) {
            // Show confirmation dialog only for trip mode
            setConfirmationDialogAction('deactivate_checkin_flow');
        } else {
            handleSettingsChange('enabled', enabled);
        }
    };

    const doDeactivateCheckInFlow = () => {
        handleSettingsChange('enabled', false);
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };

    // CP-8423: GO TO WAIVERS opens a new tab, so it carries the OpenInNew icon.
    const confirmationOkIcon = ['no_waivers', 'no_enabled_waivers', 'all_waivers_added'].includes(confirmationDialogAction) ? <OpenInNewIcon /> : undefined;

    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'deactivate_checkin_flow':
                doDeactivateCheckInFlow();
                break;
            case 'no_waivers':
            case 'no_enabled_waivers':
            case 'all_waivers_added':
                // New tab, like OPEN SETTINGS and the per-row open icon: the
                // trip form may hold unsaved edits.
                window.open('/waivers', '_blank');
                break;
            case 'waivers_load_failed':
                setWaiversReloadKey((key) => key + 1);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleFieldChange = (fieldType, fieldId, property, value) => {
        // Use preCheckSettings as source of current fields
        const currentFields = preCheckSettings[fieldType] || [];
        const fields = [...currentFields];
        const fieldIndex = fields.findIndex(f => f.id === fieldId);

        if (fieldIndex >= 0) {
            fields[fieldIndex] = {
                ...fields[fieldIndex],
                [property]: value,
            };
        }

        handleSettingsChange(fieldType, fields);
    };

    const handleDragEnd = (result, fieldType) => {
        if (!result.destination) return;

        // Use preCheckSettings as source of current items
        const currentItems = preCheckSettings[fieldType] || [];
        const items = Array.from(currentItems);

        // The custom note stays last (CP-6580): it does not move, and nothing goes below it
        const lastItem = items[items.length - 1];
        if (items[result.source.index]?.id === 'custom_note'
            || (lastItem?.id === 'custom_note' && result.destination.index === items.length - 1)) {
            return;
        }

        // For party members, keep is_minor field first (if present) for UX reasons
        if (fieldType === 'party_member_fields') {
            const draggedItem = items[result.source.index];

            // Don't allow moving is_minor field
            if (draggedItem.id === 'is_minor') {
                return;
            }

            // Don't allow moving other fields to position 0 (where is_minor should be)
            if (result.destination.index === 0) {
                const firstItem = items[0];
                if (firstItem && firstItem.id === 'is_minor') {
                    return;
                }
            }
        }

        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        handleSettingsChange(fieldType, items);
    };

    // A field the guest form always asks in the same place -- is_minor first (party members), the
    // custom note last -- gets a faded handle and does not drag. The custom note's row goes after the
    // list's placeholder, which takes the dragged row's space, so it stays put during a drag.
    const renderPinnedField = (field, fieldType) => {
        if (!field) return null;
        const kind = fieldType === 'lead_booker_fields' ? 'lead_booker' : 'party_member';
        return (
            <Box key={field.id} className={classes.fieldItem}>
                <Box display="flex" alignItems="center" gap={0.75}>
                    <div>
                        <DragIndicatorIcon
                            color="disabled"
                            sx={{ cursor: 'default' }}
                        />
                    </div>
                    <Box>
                        <Typography>{field.name}</Typography>
                    </Box>
                </Box>
                <Box className={classes.fieldControls}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={field.required}
                                onChange={(e) => handleFieldChange(fieldType, field.id, 'required', e.target.checked)}
                                disabled={!hasWritePerm || isFieldMandatory(field.id, kind) || field.type === 'tag_category' || field.id === 'is_minor' || waiversLoading}
                                size="small"
                            />
                        }
                        label="Required"
                        sx={{
                            '& .MuiFormControlLabel-label': { fontSize: '14px' },
                            [theme.breakpoints.down('sm')]: {
                                marginRight: 0
                            }
                        }}
                    />
                    <DeleteIconButton
                        fontSize="medium"
                        disabled={!hasWritePerm || isFieldMandatory(field.id, kind) || waiversLoading}
                        onClick={() => handleFieldDelete(fieldType, field.id)}
                    />
                </Box>
            </Box>
        );
    };

    const handleEditingFieldToggle = (fieldId) => {
        setEditingFields(prevFields =>
            prevFields.map(field => {
                if (field.id === fieldId) {
                    return { ...field, enabled: !field.enabled };
                }
                return field;
            })
        );
        setHasUnsavedChanges(true);
    };

    const saveFieldsEditorChanges = () => {
        // Validation: if tag categories are enabled, at least one category must be selected
        if (editingTagCategoriesEnabled && editingSelectedTagCategories.length === 0) {
            alert('Please select at least one tag category or disable the tag categories option.');
            return;
        }

        // We'll validate with the final updatedTrip data later

        const isLead = currentEditingField === 'lead_booker';
        const fieldType = isLead ? 'lead_booker_fields' : 'party_member_fields';

        // Apply only adds and removes (F6, plan 2026-10-08-console-precheck-in-editor): the fields
        // that stay keep their order, labels and flags, custom fields included, and a row the panel
        // does not show is kept as it is.
        const questions = {
            offered: offeredQuestionCodes,
            selected: editingTagCategoriesEnabled
                ? editingSelectedTagCategories.filter((categoryId) => categoryId != null && categoryId !== '')
                : [],
            make: makeQuestionRow,
        };
        const isMandatory = (field) => !field.custom && isFieldMandatory(field.id, currentEditingField);
        let fieldsToSave = applyFieldsPanel(preCheckSettings[fieldType] || [], editingFields, questions, isMandatory);
        if (!isLead) {
            fieldsToSave = minorFirst(fieldsToSave);
        }
        fieldsToSave = customNoteLast(fieldsToSave);

        // The questions bookkeeping follows the questions the list now asks -- including those kept
        // as they are because the panel cannot offer their category: these arrays are legacy
        // bookkeeping (nothing guest-facing reads them; the questions are the rows), so they list
        // what the form asks rather than only what the panel manages.
        const questionCodesOf = (list) => list.filter(isQuestionRow).map(questionCode);
        const savedCodes = questionCodesOf(fieldsToSave);

        const updates = {
            [fieldType]: fieldsToSave,
            apply_same_fields_to_party_members: editingApplySameFields,
            ...(isLead
                ? {
                    lead_tag_categories_enabled: savedCodes.length > 0,
                    lead_selected_tag_categories: savedCodes,
                }
                : {
                    party_tag_categories_enabled: savedCodes.length > 0,
                    party_selected_tag_categories: savedCodes,
                }),
        };

        // If editing lead booker and "apply same fields" is enabled, also update party member fields
        if (isLead && editingApplySameFields) {
            const partyFields = sameFieldsForParty(fieldsToSave, preCheckSettings.party_member_fields || [], offeredQuestionCodes);
            const partyCodes = questionCodesOf(partyFields);
            updates.party_member_fields = partyFields;
            updates.party_tag_categories_enabled = partyCodes.length > 0;
            updates.party_selected_tag_categories = partyCodes;
        }

        // Apply all changes at once using preCheckSettings as base, while stripping legacy keys
        const { tag_categories_enabled: _legacyTCE, selected_tag_categories: _legacySTC, ...preCheckSettingsWithoutLegacy } = preCheckSettings || {};
        const updatedSettings = {
            ...preCheckSettingsWithoutLegacy,
            ...updates
        };

        const updatedTrip = {
            ...trip,
            pre_check_settings: updatedSettings,
            ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
            _changed: true,
        };

        // Update local state
        onChange(updatedTrip);

        // Mark that we need to save after state update (only in trip mode)
        pendingSaveRef.current = !isOperatorMode;

        // Reset unsaved changes after saving
        setHasUnsavedChanges(false);

        // Close the editor with animation
        setIsFieldsEditorAnimating(false);

        setTimeout(() => {
            setFieldsEditorOpen(false);
            setCurrentEditingField(null);
            setTagCategoriesExpanded(false);

            // Reset editing state
            setEditingFields([]);
            setEditingTagCategoriesEnabled(false);
            setEditingSelectedTagCategories([]);
            setEditingApplySameFields(false);
        }, 300);
    };

    const handleAddWaiver = () => {
        if (waiversLoadFailed) {
            setConfirmationDialogAction('waivers_load_failed');
            return;
        }
        if (waivers.length === 0) {
            setConfirmationDialogAction(hasDisabledWaivers ? 'no_enabled_waivers' : 'no_waivers');
            return;
        }

        // A waiver can sit on this form once (each row's Select hides the
        // others' picks), so a row is only worth adding while some enabled
        // waiver is not on the form yet. Compare the sets, not the counts: a
        // row whose waiver has since been disabled or deleted must not block
        // the ones that are still available.
        const currentWaivers = preCheckSettings.waivers || [];
        const usedWaiverIds = currentWaivers
            .map((w) => w.waiver_id)
            .filter((id) => id != null && id !== '');
        // Row ids come back as strings from the Select, waiver ids as numbers.
        const hasUnusedWaiver = waivers.some((w) => !usedWaiverIds.some((used) => String(used) === String(w.id)));
        if (!hasUnusedWaiver) {
            setConfirmationDialogAction('all_waivers_added');
            return;
        }

        const newWaiver = {
            id: `waiver_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            waiver_id: '',
            tags: [],
        };

        const updatedSettings = {
            ...preCheckSettings,
            waivers: [...(preCheckSettings.waivers || []), newWaiver],
        };

        onChange({
            ...trip,
            pre_check_settings: updatedSettings,
            ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
            _changed: true,
        });
        setHasUnsavedChanges(true);
    };

    const handleRemoveWaiver = (waiverId) => {
        // Use preCheckSettings as source of current waivers
        const currentWaivers = preCheckSettings.waivers || [];
        const updatedWaivers = currentWaivers.filter(w => w.id !== waiverId);

        const updatedSettings = {
            ...preCheckSettings,
            waivers: updatedWaivers,
        };

        onChange({
            ...trip,
            pre_check_settings: updatedSettings,
            ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
            _changed: true,
        });
    };

    const handleWaiverChange = useCallback((waiverId, field, value) => {
        onChange(prevTrip => {
            const currentSettings = prevTrip.pre_check_settings || {};
            const currentWaivers = currentSettings.waivers || [];

            const updatedWaivers = currentWaivers.map(waiver => {
                if (waiver.id === waiverId) {
                    return {
                        ...waiver,
                        [field]: value,
                    };
                }
                return waiver;
            });

            const updatedSettings = {
                ...currentSettings,
                waivers: updatedWaivers,
            };

            return {
                ...prevTrip,
                pre_check_settings: updatedSettings,
                ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
                _changed: true,
            };
        });
    }, [onChange]);

    const handleWaiverDragEnd = (result) => {
        if (!result.destination) return;

        // Use preCheckSettings as source of current waivers
        const currentWaivers = preCheckSettings.waivers || [];
        const items = Array.from(currentWaivers);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        handleSettingsChange('waivers', items);
    };

    const openFieldsEditor = (fieldType) => {
        setCurrentEditingField(fieldType);
        setFieldsEditorOpen(true);
        setHasUnsavedChanges(false); // Reset unsaved changes when opening editor

        // Start animation after a small delay to ensure DOM is ready
        setTimeout(() => {
            setIsFieldsEditorAnimating(true);
        }, 10);

        // Initialize editing state with current values
        // Use preCheckSettings as source of current fields
        const currentFieldsData = preCheckSettings[fieldType === 'lead_booker' ? 'lead_booker_fields' : 'party_member_fields'] || [];

        // Separate regular fields from tag category fields
        const regularFieldsData = currentFieldsData.filter(f => !isQuestionRow(f));

        // Create editing fields array with all possible fields
        const allFieldTypes = getAllFieldTypes(fieldType);

        const standardEditingFields = allFieldTypes.map(fieldTemplate => {
            // Find existing field by exact ID match
            const existingField = regularFieldsData.find(f => f.id === fieldTemplate.id);

            return {
                ...fieldTemplate,
                enabled: existingField ? true : (isFieldMandatory(fieldTemplate.id, fieldType) || false),
                required: existingField ? existingField.required : (fieldTemplate.required || false)
            };
        });
        // The custom fields of this list, then (Settings > Pre-check-in) the operator's others.
        const definitions = isOperatorMode ? preCheckSettings.custom_field_definitions : [];
        const initEditingFields = [...standardEditingFields, ...customPanelRows(currentFieldsData, definitions)];

        setEditingFields(initEditingFields);

        // Initialize tag categories from the questions on the page the panel can offer (the
        // others stay as they are on Apply)
        const offeredQuestionRows = currentFieldsData.filter(f => isQuestionRow(f) && offeredQuestionCodes.has(questionCode(f)));
        const roleEnabled = offeredQuestionRows.length > 0;
        const roleSelected = offeredQuestionRows.map(questionCode);

        setEditingTagCategoriesEnabled(roleEnabled);
        setEditingSelectedTagCategories(roleSelected);
        setEditingApplySameFields(preCheckSettings.apply_same_fields_to_party_members || false);
        setFieldsEditorBaseline(fieldsEditorSnapshot(initEditingFields, roleEnabled, roleSelected, preCheckSettings.apply_same_fields_to_party_members || false));
    };

    // Track changes in tag categories enabled/disabled
    const handleEditingTagCategoriesEnabledChange = (enabled) => {
        setEditingTagCategoriesEnabled(enabled);
        setHasUnsavedChanges(true);
        if (!enabled) {
            setTagCategoriesExpanded(false);
        } else {
            // Auto-expand when enabled
            setTagCategoriesExpanded(true);
        }
    };

    // Track changes in apply same fields option
    const handleEditingApplySameFieldsChange = (apply) => {
        setEditingApplySameFields(apply);
        setHasUnsavedChanges(true);
    };

    const closeFieldsEditor = () => {
        // Check if there are unsaved changes and show confirmation
        if (hasUnsavedChanges) {
            const shouldDiscard = window.confirm("Are you sure you want to leave without saving changes?");
            if (!shouldDiscard) {
                return;
            }
        }

        // Start closing animation
        setIsFieldsEditorAnimating(false);

        // Wait for animation to complete before hiding the editor
        setTimeout(() => {
            setFieldsEditorOpen(false);
            setCurrentEditingField(null);
            setTagCategoriesExpanded(false);

            // Reset editing state
            setEditingFields([]);
            setEditingTagCategoriesEnabled(false);
            setEditingSelectedTagCategories([]);
            setEditingApplySameFields(false);
            setHasUnsavedChanges(false);
        }, 300); // Match the transition duration
    };

    // Enhanced field deletion that syncs with fields editor
    const handleFieldDelete = (fieldType, fieldId) => {
        // Use preCheckSettings as source of current fields
        const currentFields = preCheckSettings[fieldType] || [];
        const updatedFields = currentFields.filter(f => f.id !== fieldId);
        const deletedField = currentFields.find(f => f.id === fieldId);

        // If fields editor is open and editing the same field type, sync the changes
        if (fieldsEditorOpen && currentEditingField === fieldType.replace('_fields', '')) {
            // Update editing fields to reflect the deletion
            setEditingFields(prevFields =>
                prevFields.map(field => {
                    if (field.id === fieldId || field.id === fieldId.replace(/_\d+$/, '')) {
                        return { ...field, enabled: false };
                    }
                    return field;
                })
            );

            // If it's a tag category field, also update tag categories selection
            if (deletedField && deletedField.type === 'tag_category') {
                setEditingSelectedTagCategories(prev =>
                    prev.filter(catId => catId !== deletedField.category_id)
                );

                // Check if all tag category fields are deleted
                const remainingTagCategoryFields = updatedFields.filter(f => f.type === 'tag_category');
                if (remainingTagCategoryFields.length === 0) {
                    setEditingTagCategoriesEnabled(false);
                }
            }
        }

        // Persist deletion. For tag category fields, also sync selected arrays and enabled flag
        if (deletedField && deletedField.type === 'tag_category') {
            const isLead = fieldType === 'lead_booker_fields';
            const selectedKey = isLead ? 'lead_selected_tag_categories' : 'party_selected_tag_categories';
            const enabledKey = isLead ? 'lead_tag_categories_enabled' : 'party_tag_categories_enabled';

            const currentSelected = preCheckSettings[selectedKey] || [];
            const newSelected = currentSelected.filter(catId => catId !== deletedField.category_id);
            const remainingTagCategoryFields = updatedFields.filter(f => f.type === 'tag_category');
            const newEnabled = remainingTagCategoryFields.length > 0 ? preCheckSettings[enabledKey] : false;

            const updatedSettings = {
                ...preCheckSettings,
                [fieldType]: updatedFields,
                [selectedKey]: newSelected,
                [enabledKey]: newEnabled,
            };

            onChange({
                ...trip,
                pre_check_settings: updatedSettings,
                ...(isOperatorMode ? {} : { pre_check_settings_own: updatedSettings }),
                _changed: true,
            });
        } else {
            handleSettingsChange(fieldType, updatedFields);
        }
    };

    // Trip-level toggle to use operator-level settings
    const useOperatorSettings = toBool(trip.use_operator_pre_check_settings);
    const shouldShowTripSections = isOperatorMode || !useOperatorSettings;

    const handleUseOperatorToggle = (checked) => {
        const updatedTrip = {
            ...trip,
            use_operator_pre_check_settings: checked,
            // When using operator settings, we still keep current enabled flag untouched in own settings
            // UI will hide own settings but save should preserve current enabled if user toggled it before
            _changed: true,
        };
        onChange(updatedTrip);
    };

    // If pre-check-in flow disabled show compact panel in trip mode only (same layout as enabled to avoid jumps)
    if (!preCheckSettings.enabled && !isOperatorMode) {
        return (
            <div className={classes.root}>
                {confirmationDialogAction ? (
                    <ConfirmationDialog
                        keepMounted
                        onCancel={confirmationDialogCancel}
                        onConfirm={confirmationDialogConfirm}
                        title={ConfirmationDialogText[confirmationDialogAction].title}
                        okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                        cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                        okButtonIcon={confirmationOkIcon}
                        standardActions
                        open={!!confirmationDialogAction}
                    >
                        {ConfirmationDialogText[confirmationDialogAction].body}
                    </ConfirmationDialog>
                ) : null}
                <Box className={classes.styledBox}>
                    <Grid container direction="row" spacing={1.5}>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" fontWeight={500}>General settings</Typography>
                            <Button variant="outlined" onClick={() => window.open('/settings/checkin/', '_blank')} sx={{ whiteSpace: 'nowrap' }}>
                                OPEN SETTINGS
                            </Button>
                        </Grid>
                        <Grid item xs={12} container>
                            <Grid item xs={12} md={8}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={useOperatorSettings}
                                            onChange={(e) => handleUseOperatorToggle(e.target.checked)}
                                            disabled={!hasWritePerm}
                                            color="primary"
                                        />
                                    }
                                    label="Use Tour Operator default settings"
                                />
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {useOperatorSettings ? 'Settings are inherited from defaults. Turn this off to customize them for this specific trip.' : 'You can customize settings below. Turn this on to use operator defaults.'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4} />
                        </Grid>
                    {(isOperatorMode || (!isOperatorMode && !useOperatorSettings)) && (
                        <Grid item xs={12} sx={{ mt: 1, minHeight: '40px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preCheckSettings.enabled}
                                        onChange={(e) => handleCheckInFlowToggle(e.target.checked)}
                                        disabled={!hasWritePerm}
                                        color="primary"
                                    />
                                }
                                label={isOperatorMode ? 'Activate pre-check-in flow for all trips that inherit default settings' : 'Activate pre-check-in flow for this trip'}
                            />
                        </Grid>
                    )}
                    </Grid>
                </Box>
            </div>
        );
    }

    // No special return when using operator settings in trip mode: keep layout identical and just hide editable controls

    return (
        <div className={classes.root} style={{ position: 'relative' }}>
            {waiversLoading && (
                <div className={classes.loadingOverlay}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <CircularProgress />
                        <Typography variant="body2" color="textSecondary">
                            Loading waiver settings...
                        </Typography>
                    </Box>
                </div>
            )}
            {/* Not gated on trip mode: the Add Waiver prompts are raised from
                Settings > Pre-check-in too (deactivation only ever asks in trip mode). */}
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    okButtonIcon={confirmationOkIcon}
                    standardActions
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            {isOperatorMode && applyToAllOpen ? (() => {
                const n = trip.pre_check_settings?.trips_with_own_settings_count ?? 0;
                return (
                    <ConfirmationDialog
                        keepMounted
                        onCancel={() => !applyingToAll && setApplyToAllOpen(false)}
                        onConfirm={() => {
                            if (applyingToAll) return;
                            setApplyingToAll(true);
                            Promise.resolve(onApplyToAllTrips())
                                .finally(() => {
                                    setApplyingToAll(false);
                                    setApplyToAllOpen(false);
                                });
                        }}
                        title={`Apply these defaults to ${n} trip${n === 1 ? '' : 's'}?`}
                        okButtonText="Apply"
                        cancelButtonText="Cancel"
                        standardActions
                        open={applyToAllOpen}
                    >
                        <Typography variant="body2" component="div">
                            <p style={{ marginTop: 0 }}>
                                {n === 1 ? 'This trip turns' : 'Each of these trips turns'} on &quot;Use Tour Operator default settings&quot; and uses the pre-check-in settings on this page.{' '}
                                {preCheckSettings.enabled
                                    ? 'Pre-check-in will be on for all of them.'
                                    : 'Pre-check-in is off on this page, so it will be off for all of them.'}
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                Their own settings are kept. To use them again on a trip, turn &quot;Use Tour Operator default settings&quot; off on that trip&apos;s Pre-check-in tab.
                            </p>
                        </Typography>
                    </ConfirmationDialog>
                );
            })() : null}
            {/* General Settings */}
            <Box className={classes.styledBox} sx={{ borderRadius: isOperatorMode ? '12px' : '0 0 12px 12px', ...(isOperatorMode ? { pt: 1.5 } : {}) }}>
                <Grid container direction="row" spacing={1.5}>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight={500}>General settings</Typography>
                        {!isOperatorMode && (
                            <Button
                                variant="outlined"
                                onClick={() => window.open('/settings/checkin/', '_blank')}
                                disabled={waiversLoading}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                OPEN SETTINGS
                            </Button>
                        )}
                    </Grid>
                    {isOperatorMode && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 0.5 }}>
                                General settings for your pre-check-in form to tailor it to your needs and preferences.
                            </Typography>
                        </Grid>
                    )}
                    {!isOperatorMode && (
                        <Grid item xs={12} md={8}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useOperatorSettings}
                                        onChange={(e) => handleUseOperatorToggle(e.target.checked)}
                                        disabled={!hasWritePerm || waiversLoading}
                                        color="primary"
                                    />
                                }
                                label="Use Tour Operator default settings"
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                Settings are inherited from defaults. Turn this off to customize them for this specific trip.
                            </Typography>
                        </Grid>
                    )}

                    {shouldShowTripSections && (
                        <Grid item xs={12} sx={{ mt: 1, minHeight: '40px' }}>
                            {(() => {
                                const n = trip.pre_check_settings?.trips_with_own_settings_count ?? 0;
                                const label = isOperatorMode ? 'Activate pre-check-in flow for all trips that inherit default settings' : 'Activate pre-check-in flow for this trip';
                                const toggle = (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={preCheckSettings.enabled}
                                                onChange={(e) => handleCheckInFlowToggle(e.target.checked)}
                                                disabled={!hasWritePerm}
                                                color="primary"
                                            />
                                        }
                                        label={label}
                                    />
                                );
                                const known = trip.pre_check_settings?.trips_with_own_settings_count != null;
                                if (!isOperatorMode || !known) {
                                    return toggle;
                                }
                                const applyButton = (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!!applyToAllTripsBlocked || applyingToAll}
                                        onClick={() => setApplyToAllOpen(true)}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Apply to all trips
                                    </Button>
                                );
                                return (
                                    <>
                                        {toggle}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {n === 0
                                                    ? 'All trips use these defaults.'
                                                    : `${n} trip${n === 1 ? ' uses its' : 's use their'} own pre-check-in settings instead of these defaults.`}
                                            </Typography>
                                            {n > 0 && onApplyToAllTrips && (applyToAllTripsBlocked ? (
                                                <Tooltip title={applyToAllTripsBlocked}>
                                                    <span>{applyButton}</span>
                                                </Tooltip>
                                            ) : applyButton)}
                                        </Box>
                                    </>
                                );
                            })()}
                        </Grid>
                    )}

                    {shouldShowTripSections && (
                        <Grid item xs={12} sx={{ marginTop: '-12px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preCheckSettings.required}
                                        onChange={(e) => handleSettingsChange('required', e.target.checked)}
                                        disabled={!hasWritePerm || waiversLoading}
                                        color="primary"
                                    />
                                }
                                label="User is required to complete pre-check-in"
                            />
                        </Grid>
                    )}

                    {shouldShowTripSections && (
                        <Grid item xs={12} md={8}>
                            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                                Descriptive intro text
                            </Typography>
                            <TextField
                                fullWidth
                                variant="filled"
                                size="small"
                                label="Add description"
                                multiline
                                minRows={3}
                                value={preCheckSettings.intro_text}
                                onChange={(e) => handleSettingsChange('intro_text', e.target.value)}
                                disabled={!hasWritePerm || waiversLoading}
                                placeholder="Provide participating guests' contact information to complete pre-check-in."
                            />
                        </Grid>
                    )}

                    {shouldShowTripSections && (
                        <>
                            <Grid item xs={12} md={8}>
                                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1, lineHeight: '1.4' }}>
                                    Write confirmation message after user completes all steps
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    size="small"
                                    label="Add confirmation message"
                                    multiline
                                    minRows={3}
                                    value={preCheckSettings.confirmation_message}
                                    onChange={(e) => handleSettingsChange('confirmation_message', e.target.value)}
                                    onSelect={(e) => setConfirmSelection({ start: e.target.selectionStart, end: e.target.selectionEnd })}
                                    onClick={(e) => setConfirmSelection({ start: e.target.selectionStart, end: e.target.selectionEnd })}
                                    onKeyUp={(e) => setConfirmSelection({ start: e.target.selectionStart, end: e.target.selectionEnd })}
                                    inputRef={confirmInputRef}
                                    disabled={!hasWritePerm || waiversLoading}
                                    placeholder={'Confirmation email has been sent to your registered email. No further action is required at this time.\nEnjoy your trip!'}
                                    sx={{
                                        '& .MuiInputBase-input': { whiteSpace: 'pre-line' },
                                        '& .MuiInputBase-input::placeholder': { whiteSpace: 'pre-line' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                <Tooltip title={hasVariables ? '' : 'Variables are not available'}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            onClick={openConfirmVariablesMenu}
                                            disabled={!hasWritePerm || waiversLoading || !hasVariables}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            Insert variable
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>

            {/* Spacer to keep sticky save bar at viewport bottom when only defaults toggle is visible (trip mode only) */}
            {!isOperatorMode && !shouldShowTripSections && (
                <Box sx={{ minHeight: 'calc(100vh - 260px)' }} />
            )}

            {shouldShowTripSections && isSmallScreen && (
                <Divider />
            )}

            {/* Lead Booker Information */}
            {shouldShowTripSections && (
            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{
                            mb: 1,
                            [theme.breakpoints.down('sm')]: {
                                alignItems: 'flex-start'
                            }
                        }}>
                            <Typography variant="h6" fontWeight={500} sx={{
                                [theme.breakpoints.down('sm')]: {
                                    fontSize: '18px'
                                }
                            }}>
                                Lead Booker info
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openFieldsEditor('lead_booker')}
                                disabled={!hasWritePerm || waiversLoading}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    [theme.breakpoints.down('sm')]: {
                                        fontSize: '14px'
                                    }
                                }}
                            >
                                Edit Fields
                            </Button>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{
                            [theme.breakpoints.down('sm')]: {
                                fontSize: '12px',
                                alignSelf: 'flex-start'
                            }
                        }}>
                            Add, remove and reorder any of the fields that you wish to collect from lead booker.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <DragDropContext onDragEnd={waiversLoading ? () => { } : (result) => handleDragEnd(result, 'lead_booker_fields')}>
                            <Droppable droppableId="lead-booker-fields">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {preCheckSettings.lead_booker_fields.map((field, index) => {
                                            // is_minor is party-only; the custom note is pinned below the placeholder
                                            if (field.id === 'is_minor' || field.id === 'custom_note') {
                                                return null;
                                            }

                                            // Render draggable fields (excluding is_minor and custom_note)
                                            return (
                                                <Draggable key={field.id} draggableId={field.id} index={index} isDragDisabled={waiversLoading}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                            }}
                                                        >
                                                            <Box className={classes.fieldItem}>
                                                                <Box display="flex" alignItems="center" gap={0.75}>
                                                                    <div {...provided.dragHandleProps}>
                                                                        <DragIndicatorIcon
                                                                            color="primary"
                                                                            sx={{ cursor: waiversLoading ? 'default' : 'grab' }}
                                                                        />
                                                                    </div>
                                                                    <Box>
                                                                        <Typography>{field.name}</Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box className={classes.fieldControls}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={field.required}
                                                                                onChange={(e) => handleFieldChange('lead_booker_fields', field.id, 'required', e.target.checked)}
                                                                                disabled={!hasWritePerm || isFieldMandatory(field.id, 'lead_booker') || field.type === 'tag_category' || waiversLoading}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label="Required"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': { fontSize: '14px' },
                                                                            [theme.breakpoints.down('sm')]: {
                                                                                marginRight: 0
                                                                            }
                                                                        }}
                                                                    />
                                                                    <DeleteIconButton
                                                                        fontSize="medium"
                                                                        disabled={!hasWritePerm || isFieldMandatory(field.id, 'lead_booker') || waiversLoading}
                                                                        onClick={() => handleFieldDelete('lead_booker_fields', field.id)}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                        {renderPinnedField(preCheckSettings.lead_booker_fields.find((field) => field.id === 'custom_note'), 'lead_booker_fields')}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={preCheckSettings.allow_edit_after_checkin}
                                    onChange={(e) => handleSettingsChange('allow_edit_after_checkin', e.target.checked)}
                                    disabled={!hasWritePerm || waiversLoading}
                                    color="primary"
                                    sx={{
                                        [theme.breakpoints.down('sm')]: {
                                            alignSelf: 'flex-start',
                                            mt: -0.5
                                        }
                                    }}
                                />
                            }
                            label="Allow attendees to edit information after pre-check-in"
                            sx={{
                                mt: 2,
                                [theme.breakpoints.down('sm')]: {
                                    pl: 0.5,
                                    alignItems: 'flex-start',
                                    verticalAlign: 'top'
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            )}

            {shouldShowTripSections && isSmallScreen && (
                <Divider />
            )}

            {/* Party Members Information */}
            {shouldShowTripSections && (
            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={3}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{
                            mb: 1,
                            [theme.breakpoints.down('sm')]: {
                                alignItems: 'flex-start'
                            }
                        }}>
                            <Typography variant="h6" fontWeight={500}>Party members info</Typography>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openFieldsEditor('party_member')}
                                disabled={!hasWritePerm || waiversLoading}
                                sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    [theme.breakpoints.down('sm')]: {
                                        fontSize: '14px'
                                    }
                                }}
                            >
                                {(preCheckSettings.party_member_fields || []).length === 0
                                    ? 'Add Field'
                                    : 'Edit Fields'}
                            </Button>
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={preCheckSettings.collect_party_members_info}
                                    onChange={(e) => handleSettingsChange('collect_party_members_info', e.target.checked)}
                                    disabled={!hasWritePerm || waiversLoading}
                                    color="primary"
                                    sx={{
                                        [theme.breakpoints.down('sm')]: {
                                            alignSelf: 'flex-start',
                                            mt: -0.5
                                        }
                                    }}
                                />
                            }
                            label="Collect information of additional party members"
                            sx={{
                                mt: 2,
                                mb: 0,
                                [theme.breakpoints.down('sm')]: {
                                    pl: 0.5,
                                    alignItems: 'flex-start',
                                    verticalAlign: 'top'
                                }
                            }}
                        />

                        <Box sx={{ display: 'block', mt: 0.5, mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={preCheckSettings.include_party_members_in_chat}
                                        onChange={(e) => handleSettingsChange('include_party_members_in_chat', e.target.checked)}
                                        disabled={!hasWritePerm || waiversLoading}
                                        color="primary"
                                        sx={{
                                            [theme.breakpoints.down('sm')]: {
                                                alignSelf: 'flex-start',
                                                mt: -0.5
                                            }
                                        }}
                                    />
                                }
                                label="Include +1 bookers in chat"
                                sx={{
                                    [theme.breakpoints.down('sm')]: {
                                        pl: 0.5,
                                        alignItems: 'flex-start',
                                        verticalAlign: 'top'
                                    }
                                }}
                            />
                        </Box>

                        <Typography variant="body2" color="textSecondary" sx={{
                            [theme.breakpoints.down('sm')]: {
                                fontSize: '12px',
                                alignSelf: 'flex-start'
                            }
                        }}>
                            Add, remove and reorder any of the fields that you wish to collect from party members.
                            Party members fields don&apos;t have to be the same as lead booker.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <DragDropContext onDragEnd={waiversLoading ? () => { } : (result) => handleDragEnd(result, 'party_member_fields')}>
                            <Droppable droppableId="party-member-fields">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {preCheckSettings.party_member_fields.map((field, index) => {
                                            // is_minor is pinned first (not draggable); the custom note below the placeholder
                                            if (field.id === 'is_minor') {
                                                return renderPinnedField(field, 'party_member_fields');
                                            }
                                            if (field.id === 'custom_note') {
                                                return null;
                                            }

                                            // Render draggable fields (excluding is_minor and custom_note)
                                            return (
                                                <Draggable key={field.id} draggableId={field.id} index={index} isDragDisabled={waiversLoading}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                            }}
                                                        >
                                                            <Box className={classes.fieldItem}>
                                                                <Box display="flex" alignItems="center" gap={0.75}>
                                                                    <div {...provided.dragHandleProps}>
                                                                        <DragIndicatorIcon
                                                                            color="primary"
                                                                            sx={{ cursor: waiversLoading ? 'default' : 'grab' }}
                                                                        />
                                                                    </div>
                                                                    <Box>
                                                                        <Typography>{field.name}</Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box className={classes.fieldControls}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={field.required}
                                                                                onChange={(e) => handleFieldChange('party_member_fields', field.id, 'required', e.target.checked)}
                                                                                disabled={!hasWritePerm || isFieldMandatory(field.id, 'party_member') || field.type === 'tag_category' || waiversLoading}
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label="Required"
                                                                        sx={{
                                                                            '& .MuiFormControlLabel-label': { fontSize: '14px' },
                                                                            [theme.breakpoints.down('sm')]: {
                                                                                marginRight: 0
                                                                            }
                                                                        }}
                                                                    />
                                                                    <DeleteIconButton
                                                                        fontSize="medium"
                                                                        disabled={!hasWritePerm || isFieldMandatory(field.id, 'party_member') || waiversLoading}
                                                                        onClick={() => handleFieldDelete('party_member_fields', field.id)}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                        {renderPinnedField(preCheckSettings.party_member_fields.find((field) => field.id === 'custom_note'), 'party_member_fields')}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Grid>
                </Grid>
            </Box>
            )}

            {shouldShowTripSections && isSmallScreen && (
                <Divider />
            )}

            {/* Waiver Settings */}
            {shouldShowTripSections && (
            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={1}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{
                            flexDirection: 'row',
                            [theme.breakpoints.down('sm')]: {
                                flexDirection: 'column',
                                gap: 2,
                            },
                        }}>
                            <Box>
                                <Typography variant="h6" fontWeight={500}>Waiver settings</Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 0 }}>
                                    If your pre-check-in form has a waiver, set it up here to ensure users acknowledge and agree to the terms before completing their pre-check-in.
                                </Typography>
                            </Box>
                            <Box sx={{ [theme.breakpoints.up('sm')]: { alignSelf: 'flex-start' } }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleSettingsChange('waiver_enabled', !preCheckSettings.waiver_enabled)}
                                    disabled={!hasWritePerm || waiversLoading}
                                    sx={{
                                        textTransform: 'uppercase',
                                        fontWeight: 500,
                                        color: preCheckSettings.waiver_enabled ? '#007AFF' : 'inherit',
                                        borderColor: preCheckSettings.waiver_enabled ? '#007AFF' : 'inherit',
                                        whiteSpace: 'nowrap',
                                    }}
                                    startIcon={
                                        <Switch
                                            checked={preCheckSettings.waiver_enabled}
                                            disabled={!hasWritePerm || waiversLoading}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                >
                                    Pre-check-in has a waiver
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    {preCheckSettings.waiver_enabled && (
                        <Grid item xs={12} md={8}>
                            <DragDropContext onDragEnd={waiversLoading ? () => { } : handleWaiverDragEnd}>
                                <Droppable droppableId="waivers">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef} style={{ width: '100%' }}>
                                            {preCheckSettings.waivers.map((waiver, index) => (
                                                <Draggable key={waiver.id} draggableId={waiver.id} index={index} isDragDisabled={waiversLoading}>
                                                    {(provided, snapshot) => {
                                                        const rowWaivers = waivers.filter((w) => {
                                                            if (w.id == waiver.waiver_id) return true;
                                                            const currentWaivers = preCheckSettings.waivers || [];
                                                            const usedWaiverIds = currentWaivers
                                                                .filter((_, i) => i !== index)
                                                                .map((other) => other.waiver_id)
                                                                .filter((id) => id != null && id !== '');
                                                            return !usedWaiverIds.some((used) => used == w.id);
                                                        });
                                                        let optionsForSelect = rowWaivers;
                                                        if (
                                                            !waiversLoading
                                                            && waiver.waiver_id != null
                                                            && waiver.waiver_id !== ''
                                                            && !rowWaivers.some((w) => w.id == waiver.waiver_id)
                                                        ) {
                                                            optionsForSelect = [
                                                                {
                                                                    id: waiver.waiver_id,
                                                                    name: `Waiver #${waiver.waiver_id} (unavailable)`,
                                                                },
                                                                ...rowWaivers,
                                                            ];
                                                        }
                                                        const selectValue = waiversLoading
                                                            ? ''
                                                            : (waiver.waiver_id != null
                                                                && waiver.waiver_id !== ''
                                                                && optionsForSelect.some((w) => w.id == waiver.waiver_id)
                                                                ? waiver.waiver_id
                                                                : '');
                                                        return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                                marginTop: index === 0 ? '8px' : '0px',
                                                            }}
                                                        >
                                                            <Box className={classes.fieldItem}>
                                                                <Box display="flex" alignItems="center" gap={0.75} sx={{
                                                                    flex: 1,
                                                                    overflow: 'hidden',
                                                                    pr: { xs: 1, sm: 2 }
                                                                }}>
                                                                    <div {...provided.dragHandleProps}>
                                                                        <DragIndicatorIcon
                                                                            color="primary"
                                                                            sx={{ cursor: 'grab' }}
                                                                        />
                                                                    </div>
                                                                    <FormControl variant="filled" size="small" sx={{
                                                                        flex: 1,
                                                                        minWidth: 0
                                                                    }}>
                                                                        <InputLabel>Select waiver</InputLabel>
                                                                        <Select
                                                                            value={selectValue}
                                                                            onChange={(e) => {
                                                                                e.preventDefault();
                                                                                handleWaiverChange(waiver.id, 'waiver_id', e.target.value);
                                                                            }}
                                                                            disabled={!hasWritePerm || waiversLoading}
                                                                            sx={{
                                                                                '& .MuiSelect-select': {
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap'
                                                                                }
                                                                            }}
                                                                        >
                                                                            {optionsForSelect.map((w) => (
                                                                                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </Box>
                                                                <Box className={classes.fieldControls}>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={!hasWritePerm || !waiver.waiver_id || waiversLoading}
                                                                        onClick={() => {
                                                                            if (waiver.waiver_id) {
                                                                                window.open(`/waivers/${waiver.waiver_id}`, '_blank');
                                                                            }
                                                                        }}
                                                                    >
                                                                        <OpenInNewIcon />
                                                                    </IconButton>
                                                                    <DeleteIconButton
                                                                        fontSize="medium"
                                                                        disabled={!hasWritePerm || waiversLoading}
                                                                        onClick={() => handleRemoveWaiver(waiver.id)}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </div>
                                                        );
                                                    }}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddWaiver}
                                    disabled={!hasWritePerm || waiversLoading}
                                    sx={{
                                        textTransform: 'uppercase',
                                        fontWeight: 500,
                                        [theme.breakpoints.down('sm')]: {
                                            width: '100%'
                                        }
                                    }}
                                >
                                    Add Waiver
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
            )}

            {/* Fields Editor Overlay */}
            {fieldsEditorOpen && (
                <>
                    <div
                        className={`${classes.overlay} ${isFieldsEditorAnimating ? classes.overlayVisible : ''}`}
                        onClick={() => {
                            closeFieldsEditor();
                        }}
                    />
                    <Box className={`${classes.fieldsEditor} ${isFieldsEditorAnimating ? classes.fieldsEditorOpen : ''}`}>
                        {/* Header */}
                        <Box className={classes.fieldsEditorHeader}>
                            <Typography variant="h6" fontWeight={500} sx={{ fontSize: '20px', color: '#000000' }}>
                                Pre-check-in fields: {currentEditingField === 'lead_booker' ? 'Lead booker' : 'Party members'}
                            </Typography>
                            <IconButton onClick={closeFieldsEditor} sx={{ color: '#000000' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box className={classes.fieldsEditorContent}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                Add, remove and reorder any of the fields that you wish to collect from {currentEditingField === 'lead_booker' ? 'lead booker' : 'party members'}.
                            </Typography>

                            <Box>
                                {editingFields.map((field) => (
                                    <Box key={field.id} className={classes.fieldItem} sx={{ mb: 1 }}>
                                        <Box display="flex" alignItems="center" gap={0.75}>
                                            <Checkbox
                                                checked={field.enabled}
                                                onChange={() => handleEditingFieldToggle(field.id)}
                                                disabled={!hasWritePerm || (!field.custom && isFieldMandatory(field.id, currentEditingField))}
                                                size="small"
                                            />
                                            <Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography sx={{
                                                        fontSize: '16px',
                                                        fontWeight: 400,
                                                        color: '#000000'
                                                    }}>
                                                        {field.name}
                                                    </Typography>
                                                    {field.custom && (
                                                        <Chip label="Custom" size="small" variant="outlined" sx={{ height: '20px', fontSize: '12px', color: '#747475' }} />
                                                    )}
                                                </Box>
                                                {!field.custom && isFieldMandatory(field.id, currentEditingField) && (
                                                    <Typography sx={{
                                                        fontSize: '12px',
                                                        color: '#747475',
                                                        marginTop: '2px'
                                                    }}>
                                                        Mandatory for lead booker.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            {/* Tag Categories Section - only show if trip has associated tag categories */}
                            {tagCategories.length > 0 && (
                                <Box className={classes.expandableFieldItem} sx={{ mt: 2 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Checkbox
                                                checked={editingTagCategoriesEnabled}
                                                onChange={(e) => handleEditingTagCategoriesEnabledChange(e.target.checked)}
                                                disabled={!hasWritePerm}
                                                size="small"
                                            />
                                            <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#000000' }}>
                                                Create questions from tag categories
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            onClick={() => setTagCategoriesExpanded(!tagCategoriesExpanded)}
                                            disabled={!editingTagCategoriesEnabled}
                                            size="small"
                                            sx={{ color: '#000000', border: '1px solid #007AFF', borderRadius: '8px', p: '8px' }}
                                        >
                                            {tagCategoriesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Box>

                                    <Collapse in={tagCategoriesExpanded && editingTagCategoriesEnabled}>
                                        <Box className={classes.tagCategoriesDropdown}>
                                            <Typography sx={{ fontSize: '12px', color: '#747475', mb: 1 }}>
                                                Select questions to use in form:
                                            </Typography>
                                            <Autocomplete
                                                multiple
                                                value={editingSelectedTagCategories.map(categoryId => {
                                                    const category = tagCategories.find(c => (c.code || c.id) === categoryId);
                                                    return category;
                                                }).filter(Boolean)}
                                                options={tagCategories}
                                                onChange={(e, selectedCategories) => {
                                                    if (selectedCategories !== undefined) {
                                                        const selectedIds = selectedCategories.map(cat => cat.code || cat.id);
                                                        setEditingSelectedTagCategories(selectedIds);
                                                        setHasUnsavedChanges(true);
                                                    }
                                                }}
                                                disabled={!hasWritePerm}
                                                size="small"
                                                getOptionLabel={(option) => option?.name || ''}
                                                isOptionEqualToValue={(option, value) => {
                                                    if (!option || !value) return false;
                                                    const optionId = option.code || option.id;
                                                    const valueId = value.code || value.id;
                                                    return optionId === valueId;
                                                }}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            key={option.code || option.id}
                                                            label={option.name}
                                                            size="small"
                                                            {...getTagProps({ index })}
                                                            sx={{
                                                                backgroundColor: '#D8D8D8',
                                                                color: '#202020',
                                                                fontSize: '14px',
                                                                height: '22px',
                                                                '& .MuiChip-DeleteOutlinedIcon': {
                                                                    color: '#747475',
                                                                    fontSize: '16px'
                                                                }
                                                            }}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select tag categories"
                                                        variant="filled"
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            {/* Apply to Party Members Section - only for Lead Booker */}
                            {currentEditingField === 'lead_booker' && (
                                <Box className={classes.applyToPartyMembersSection}>
                                    <Switch
                                        checked={editingApplySameFields}
                                        onChange={(e) => handleEditingApplySameFieldsChange(e.target.checked)}
                                        disabled={!hasWritePerm}
                                        size="small"
                                    />
                                    <Typography sx={{
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        color: '#202020',
                                        textAlign: 'right'
                                    }}>
                                        Apply same fields to all party members
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Bottom Bar */}
                        <Box className={classes.fieldsEditorBottom}>
                            <Box /> {/* Empty spacer */}
                            {/* In the trip editor this saves the trip; on Settings > Pre-check-in
                                (operator mode) it only updates the page, whose own SAVE persists it. */}
                            <FormActionButtons
                                mode="edit"
                                size="medium"
                                submitLabel={isOperatorMode ? 'Apply' : undefined}
                                submitIcon={isOperatorMode ? null : undefined}
                                onCancel={closeFieldsEditor}
                                onSubmit={saveFieldsEditorChanges}
                                submitDisabled={!hasWritePerm || !fieldsEditorChanged}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {/* Variables menu for confirmation message */}
            <Menu anchorEl={anchorElConfirmVar} open={Boolean(anchorElConfirmVar)} onClose={closeConfirmVariablesMenu}>
                {menuPathConfirm.length > 0 && (
                    <MenuItem onClick={handleConfirmMenuBack}>
                        <Typography>{'<'}</Typography>
                        <Typography sx={{ ml: 1 }}>Back</Typography>
                    </MenuItem>
                )}
                {currentNodeConfirm && typeof currentNodeConfirm === 'object' && Object.keys(currentNodeConfirm).sort().map((key) => {
                    const value = currentNodeConfirm[key];
                    const isLeaf = !(value && typeof value === 'object');
                    return (
                        <MenuItem key={key} onClick={() => handleConfirmMenuClick(key)}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography>{key}</Typography>
                                {!isLeaf && <ChevronRightIcon fontSize="small" />}
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </div>
    );
};

TripPreCheckSettings.propTypes = {
    data: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    hasWritePerm: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['trip', 'operator']),
    onApplyToAllTrips: PropTypes.func,
    applyToAllTripsBlocked: PropTypes.string,
};

export default TripPreCheckSettings; 